import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Skeleton,
  CardHeader,
  Divider,
  useTheme,
} from '@mui/material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from 'recharts';
import api from '../utils/api';
import PeopleIcon from '@mui/icons-material/People';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ForumIcon from '@mui/icons-material/Forum';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';

const StatCard = ({ title, value, icon, loading, error }) => (
  <Card sx={{ display: 'flex', alignItems: 'center', p: 2, height: '100%' }}>
    {icon && React.cloneElement(icon, { sx: { fontSize: 40, color: 'primary.main', mr: 2 } })}
    <Box>
      {loading ? (
        <CircularProgress size={24} />
      ) : error ? (
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'error.main' }}>Error</Typography>
      ) : (
        <Typography variant="h6" sx={{ fontWeight: 600 }}>{value ?? 'N/A'}</Typography>
      )}
      <Typography variant="body2" color="text.secondary">{title}</Typography>
    </Box>
  </Card>
);

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState({ stats: true, charts: true });
  const [error, setError] = useState('');
  const theme = useTheme();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading({ stats: true, charts: true });
        const response = await api.get('/dashboard');
        const apiData = response.data;
        console.log("API Dashboard Data:", apiData);

        let usersByStatusArray = [];
        if (apiData.users_by_status && typeof apiData.users_by_status === 'object' && !Array.isArray(apiData.users_by_status)) {
          usersByStatusArray = Object.entries(apiData.users_by_status).map(([key, value]) => ({
            name: key.charAt(0).toUpperCase() + key.slice(1),
            value: value || 0
          }));
        } else if (Array.isArray(apiData.users_by_status)) {
          usersByStatusArray = apiData.users_by_status;
        }

        let usersByTierArray = [];
        if (apiData.users_by_tier && typeof apiData.users_by_tier === 'object' && !Array.isArray(apiData.users_by_tier)) {
           usersByTierArray = Object.entries(apiData.users_by_tier).map(([key, value]) => ({
              name: key.charAt(0).toUpperCase() + key.slice(1),
              value: value || 0
           }));
        } else if (Array.isArray(apiData.users_by_tier)) {
           usersByTierArray = apiData.users_by_tier;
        }

        console.log("[Debug] Value for total_messages BEFORE setData:", apiData.total_messages);
        console.log("[Debug] Value for signup_trend BEFORE setData:", apiData.signup_trend);
        console.log("[Debug] Is apiData.signup_trend an Array?:", Array.isArray(apiData.signup_trend));

        const signupTrend = Array.isArray(apiData.signup_trend) ? apiData.signup_trend : [];
        console.log("Processed signupTrend:", signupTrend);

        setData({
          ...apiData,
          users_by_status: usersByStatusArray,
          users_by_tier: usersByTierArray,
          signup_trend: Array.isArray(apiData.signup_trend) ? apiData.signup_trend : [],
          total_chat_sessions: apiData.total_chat_sessions === "Error" ? null : apiData.total_chat_sessions,
          total_messages: apiData.total_messages === "Error" ? null : apiData.total_messages,
        });
        setError('');
        setLoading({ stats: false, charts: false });
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        const detail = err.response?.data?.detail || 'Failed to load dashboard data';
        setError(detail);
        setLoading({ stats: false, charts: false });
      }
    };
    fetchData();
  }, []);

  const isLoading = loading.stats || loading.charts;

  if (isLoading && !error) {
    return (
      <Box sx={{ p: 3 }}>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {[1, 2, 3].map(i => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rectangular" height={80} />
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[1, 2, 3].map(i => (
            <Grid item xs={12} md={4} key={`chart-skel-${i}`}>
              <Skeleton variant="rectangular" height={300} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;
  }

  if (!data) {
    return <Alert severity="info" sx={{mt: 2}}>No dashboard data available.</Alert>;
  }

  // Log data right before the return statement one last time
  console.log("[Render] Data state right before return:", data);
  console.log("[Render] data.total_messages:", data.total_messages, "Type:", typeof data.total_messages);
  console.log("[Render] data.signup_trend:", data.signup_trend, "Length:", data.signup_trend?.length);

  const totalMessagesError = data.total_messages === null;

  const COLORS_TIER = [theme.palette.primary.light, theme.palette.secondary.main];
  const COLORS_STATUS = [theme.palette.success.light, theme.palette.warning.light, theme.palette.error.light, theme.palette.grey[400], theme.palette.info.light];

  const chatSessionsError = data.total_chat_sessions === null;

  console.log("Current state 'data':", data);

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>Dashboard Overview</Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Total Users"
            value={data.total_users}
            icon={<PeopleIcon />}
            loading={loading.stats}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Total Chat Sessions"
            value={data.total_chat_sessions}
            icon={<ForumIcon />}
            loading={loading.stats}
            error={chatSessionsError}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          {/* Log the props being passed to StatCard */}
          {console.log("[Render StatCard Props] value:", data.total_messages, "loading:", loading.stats, "error:", totalMessagesError)}
          <StatCard
            title="Total Messages Sent"
            value={data.total_messages}
            icon={<QuestionAnswerIcon />}
            loading={loading.stats}
            error={totalMessagesError}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '40dvh', display: 'flex', flexDirection: 'column' }}>
            <CardHeader title="Signup Trend (Last 3 Months)" titleTypographyProps={{ variant: 'h6' }} />
            <Divider />
            <CardContent sx={{ flexGrow: 1, overflow: 'auto', p: 1 }}>
              {/* Log the values used in the condition */}
              {console.log("[Render Trend Condition] loading.charts:", loading.charts, "data.signup_trend && data.signup_trend.length > 0:", data.signup_trend && data.signup_trend.length > 0)}
              {loading.charts ? <CircularProgress sx={{m: 'auto', display:'block'}} /> : data.signup_trend && data.signup_trend.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  {/* Log the data passed to LineChart */}
                  {console.log("[Render Trend Chart] data:", data.signup_trend)}
                  <LineChart data={data.signup_trend} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme.palette.divider}/>
                    <XAxis dataKey="month" axisLine={false} tickLine={false} />
                    <YAxis axisLine={false} tickLine={false}/>
                    <Tooltip />
                    <Line type="monotone" dataKey="signups" stroke={theme.palette.primary.main} strokeWidth={2} activeDot={{ r: 8 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Typography sx={{ textAlign: 'center', p: 4 }} color="text.secondary">No signup trend data available.</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '40dvh', display: 'flex', flexDirection: 'column' }}>
            <CardHeader title="Users by Status" titleTypographyProps={{ variant: 'h6' }}/>
            <Divider />
            <CardContent sx={{ flexGrow: 1, overflow: 'auto', p: 1 }}>
              {loading.charts ? <CircularProgress sx={{m: 'auto', display:'block'}} /> : data.users_by_status && data.users_by_status.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.users_by_status} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={theme.palette.divider} />
                    <XAxis type="number" axisLine={false} tickLine={false}/>
                    <YAxis type="category" dataKey="name" width={80} axisLine={false} tickLine={false}/>
                    <Tooltip />
                    <Bar dataKey="value" fill={theme.palette.info.main} barSize={20} radius={[0, 4, 4, 0]}>
                      {data.users_by_status.map((entry, index) => (
                        <Cell key={`cell-status-${index}`} fill={COLORS_STATUS[index % COLORS_STATUS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Typography sx={{ textAlign: 'center', p: 4 }} color="text.secondary">No user status data available.</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '40dvh', display: 'flex', flexDirection: 'column' }}>
            <CardHeader title="Users by Tier" titleTypographyProps={{ variant: 'h6' }}/>
            <Divider />
            <CardContent sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'auto', p: 1 }}>
              {loading.charts ? <CircularProgress /> : data.users_by_tier && data.users_by_tier.length > 0 ? (
                <ResponsiveContainer width="100%" height="90%">
                  <PieChart>
                    <Pie
                      data={data.users_by_tier}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={'80%'}
                      innerRadius={'50%'}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {data.users_by_tier.map((entry, index) => (
                        <Cell key={`cell-tier-${index}`} fill={COLORS_TIER[index % COLORS_TIER.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                     <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Typography sx={{ textAlign: 'center', p: 4 }} color="text.secondary">No user tier data available.</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Typography variant="h5" gutterBottom sx={{mt: 4}}>
        Recent Signups
      </Typography>
      <Paper>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ backgroundColor: theme.palette.background.default }}>
            <tr>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: theme.palette.text.secondary, textTransform: 'uppercase', fontSize: '0.75rem', borderBottom: `1px solid ${theme.palette.divider}` }}>Username</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: theme.palette.text.secondary, textTransform: 'uppercase', fontSize: '0.75rem', borderBottom: `1px solid ${theme.palette.divider}` }}>Email</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: theme.palette.text.secondary, textTransform: 'uppercase', fontSize: '0.75rem', borderBottom: `1px solid ${theme.palette.divider}` }}>Tier</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: theme.palette.text.secondary, textTransform: 'uppercase', fontSize: '0.75rem', borderBottom: `1px solid ${theme.palette.divider}` }}>Status</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: theme.palette.text.secondary, textTransform: 'uppercase', fontSize: '0.75rem', borderBottom: `1px solid ${theme.palette.divider}` }}>Created At</th>
            </tr>
          </thead>
          <tbody>
            {data.recent_signups && data.recent_signups.length > 0 ? data.recent_signups.map((user, index) => (
              <tr key={index} style={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
                <td style={{ padding: '12px 16px', color: theme.palette.text.primary }}>{user.username}</td>
                <td style={{ padding: '12px 16px', color: theme.palette.text.primary }}>{user.email}</td>
                <td style={{ padding: '12px 16px', color: theme.palette.text.primary }}>{user.subscription_tier}</td>
                <td style={{ padding: '12px 16px', color: theme.palette.text.primary }}>{user.subscription_status}</td>
                <td style={{ padding: '12px 16px', color: theme.palette.text.primary }}>
                  {user.created_at ? new Date(user.created_at).toLocaleString() : 'N/A'}
                </td>
              </tr>
            )) : (
              <tr><td colSpan="5" style={{ padding: '16px', textAlign: 'center', color: theme.palette.text.secondary }}>No recent signups found.</td></tr>
            )}
          </tbody>
        </table>
      </Paper>
    </Box>
  );
};

export default Dashboard;